'use client'

import { X } from 'lucide-react'
import type { ActionItem } from '@/types/radar'
import { sortActions } from '@/lib/radar/action-priority'
import { Button, IconButton } from '@/components/platform/ui'
import { Chip, InstrumentLabel, Panel } from './ui'

/**
 * Open-actions banner — count, the top three by priority, and one way in.
 *
 * Kept dismissible (the 2026-05-17 revert from a 3-card hero stands: the
 * banner must not dominate the fold), but widened from the single-line strip
 * to the reference's three-row list so the top fixes are readable without a
 * tab change.
 */
export function ActionPlan({
  actions,
  onNavigateToActions,
  onDismiss,
}: {
  actions: ActionItem[]
  onNavigateToActions: () => void
  onDismiss: () => void
}) {
  if (actions.length === 0) return null

  // Canonical order at the boundary — see ActionsPanel.
  const top = sortActions(actions).slice(0, 3)

  return (
    <Panel tone='action' className='flex flex-col sm:flex-row sm:items-stretch'>
      <div className='flex min-w-0 flex-1 flex-col gap-3.5 px-5 py-5 sm:px-7'>
        <InstrumentLabel className='!text-[var(--cta)]'>
          {/* "N high impact" removed: `impact` is hand-authored, so the
              count asserted a measurement that does not exist. */}
          {actions.length} action{actions.length === 1 ? '' : 's'} open
        </InstrumentLabel>

        <ul className='flex flex-col gap-2'>
          {top.map(action => (
            <li key={action.id} className='flex items-start gap-3 text-sm'>
              {/* Impact chip removed — same unsupported-claim reason as
                  ActionsPanel. Effort is the defensible signal here. */}
              <Chip color='var(--pm-text-muted)' className='mt-px'>
                {action.effort === 'low'
                  ? 'Quick'
                  : action.effort === 'medium'
                    ? 'Med'
                    : 'Deep'}
              </Chip>
              <span className='min-w-0'>{action.title}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className='flex items-center justify-between gap-2 border-t border-hairline px-5 py-4 sm:border-l sm:border-t-0 sm:px-7'>
        <Button variant='cta' size='md' onClick={onNavigateToActions}>
          Fix now
        </Button>
        <IconButton
          icon={X}
          aria-label='Dismiss the open actions banner'
          tooltip='Dismiss'
          variant='ghost'
          size='sm'
          onClick={onDismiss}
        />
      </div>
    </Panel>
  )
}
